import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { usePlaylistRecommendations } from "../features/submit_playlist/usePlaylistRecommendations";
import { PlaylistForm } from "../features/submit_playlist/PlaylistForm";
import { FAQSection } from "../content/FAQSection";
import { SocialProof } from "../shared/components/SocialProof";
import { LanguageSwitcher } from "../shared/components/LanguageSwitcher";
import { SITE_CONFIG } from "./config";
import { useLanguage } from "../i18n";
import { useHead } from "../seo/useHead";

// Lightweight slug sets for route matching — no content data pulled into main bundle
import { glossarySlugs, comparisonSlugs, personaSlugs, curationSlugs, guideSlugs } from "./route-slugs";

// ── Lazy-loaded page components (code-split into separate chunks) ──

// Legal pages
const TermsPage = lazy(() => import("../pages/TermsPage").then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("../pages/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
const AffiliatePage = lazy(() => import("../pages/AffiliatePage").then(m => ({ default: m.AffiliatePage })));

// Blog
const BlogIndex = lazy(() => import("../pages/blog/BlogIndex").then(m => ({ default: m.BlogIndex })));
const VinylCdVsStreaming = lazy(() => import("../pages/blog/posts/VinylCdVsStreaming").then(m => ({ default: m.VinylCdVsStreaming })));
const WhereToBuyVinylCd = lazy(() => import("../pages/blog/posts/WhereToBuyVinylCd").then(m => ({ default: m.WhereToBuyVinylCd })));
const EssentialClassicRockAlbums = lazy(() => import("../pages/blog/posts/EssentialClassicRockAlbums").then(m => ({ default: m.EssentialClassicRockAlbums })));
const ConvertSpotifyPlaylistToCd = lazy(() => import("../pages/blog/posts/ConvertSpotifyPlaylistToCd").then(m => ({ default: m.ConvertSpotifyPlaylistToCd })));
const IsVinylWorthIt = lazy(() => import("../pages/blog/posts/IsVinylWorthIt").then(m => ({ default: m.IsVinylWorthIt })));

// Long-tail SEO blog posts
const SpotifyPlaylistCostOnVinyl = lazy(() => import("../pages/blog/posts/SpotifyPlaylistCostOnVinyl").then(m => ({ default: m.SpotifyPlaylistCostOnVinyl })));
const SpotifyPlaylistToVinylConverter = lazy(() => import("../pages/blog/posts/SpotifyPlaylistToVinylConverter").then(m => ({ default: m.SpotifyPlaylistToVinylConverter })));
const HowToBuySpotifySongsOnCd = lazy(() => import("../pages/blog/posts/HowToBuySpotifySongsOnCd").then(m => ({ default: m.HowToBuySpotifySongsOnCd })));
const CostOfOwningMusicVsStreaming = lazy(() => import("../pages/blog/posts/CostOfOwningMusicVsStreaming").then(m => ({ default: m.CostOfOwningMusicVsStreaming })));
const DoYouOwnSpotifyMusic = lazy(() => import("../pages/blog/posts/DoYouOwnSpotifyMusic").then(m => ({ default: m.DoYouOwnSpotifyMusic })));
const SpotifySongsDisappearing = lazy(() => import("../pages/blog/posts/SpotifySongsDisappearing").then(m => ({ default: m.SpotifySongsDisappearing })));

// Programmatic SEO pages
const GlossaryIndex = lazy(() => import("../pages/glossary/GlossaryIndex").then(m => ({ default: m.GlossaryIndex })));
const GlossaryTermPage = lazy(() => import("../pages/glossary/GlossaryTerm").then(m => ({ default: m.GlossaryTermPage })));
const CompareIndex = lazy(() => import("../pages/compare/CompareIndex").then(m => ({ default: m.CompareIndex })));
const ComparePage = lazy(() => import("../pages/compare/ComparePage").then(m => ({ default: m.ComparePage })));
const PersonaPage = lazy(() => import("../pages/personas/PersonaPage").then(m => ({ default: m.PersonaPage })));
const BestIndex = lazy(() => import("../pages/best/BestIndex").then(m => ({ default: m.BestIndex })));
const BestPage = lazy(() => import("../pages/best/BestPage").then(m => ({ default: m.BestPage })));
const GuideIndex = lazy(() => import("../pages/guides/GuideIndex").then(m => ({ default: m.GuideIndex })));
const GuidePage = lazy(() => import("../pages/guides/GuidePage").then(m => ({ default: m.GuidePage })));

// Heavy feature component (only needed after form submission)
const RecommendationsSection = lazy(() =>
  import("../features/recommendations/RecommendationsSection").then(m => ({ default: m.RecommendationsSection }))
);

/** Ensures homepage has proper <head> meta tags (resets after subpage navigation). */
const HOMEPAGE_HREFLANG = [
  { lang: "en", href: `https://${SITE_CONFIG.domain}/` },
  { lang: "de", href: `https://${SITE_CONFIG.domain}/` },
  { lang: "fr", href: `https://${SITE_CONFIG.domain}/` },
  { lang: "pl", href: `https://${SITE_CONFIG.domain}/` },
  { lang: "x-default", href: `https://${SITE_CONFIG.domain}/` },
];

const HomeHead = () => {
  useHead({
    title: "Turn Spotify Playlists into Vinyl & CD Shopping Lists",
    description:
      "Turn your Spotify playlist into a vinyl & CD shopping list. Find where to buy physical records, CDs and vinyls with real Discogs prices. Own your music collection forever. Free, no login.",
    path: "/",
    ogType: "website",
    hreflang: HOMEPAGE_HREFLANG,
  });
  return null;
};

const PageFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <span style={{ fontSize: "2rem" }}>💿</span>
  </div>
);

const normalizePath = (p: string): string =>
  p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

const useSimpleRouter = () => {
  const [path, setPath] = useState(normalizePath(window.location.pathname));

  useEffect(() => {
    const handlePopState = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return path;
};

function App(): JSX.Element {
  const currentPath = useSimpleRouter();
  const { t } = useLanguage();
  const [lastPlaylistRef, setLastPlaylistRef] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const {
    mutate: submitPlaylist,
    data,
    isPending,
    isError,
    error,
    reset
  } = usePlaylistRecommendations();

  // Load shared playlist from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const playlistId = params.get("playlist");
    if (playlistId) {
      setLastPlaylistRef(playlistId);
      submitPlaylist({ playlistRef: playlistId });
    }
  }, []);

  // Update URL when we get results
  useEffect(() => {
    if (data?.playlistId) {
      const url = new URL(window.location.href);
      url.searchParams.set("playlist", data.playlistId);
      window.history.replaceState({}, "", url.toString());
    }
  }, [data?.playlistId]);
  
  // Auto-scroll to results when data is loaded
  useEffect(() => {
    if (data && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [data]);

  // Initialize AdSense ads
  useEffect(() => {
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch (_) { /* ad blocker */ }
  }, []);

  const handleSubmit = (payload: { playlistRef: string; turnstileToken?: string }) => {
    setLastPlaylistRef(payload.playlistRef);
    submitPlaylist(payload);
  };

  const handleReset = () => {
    setLastPlaylistRef(null);
    reset();
    // Clear URL params
    const url = new URL(window.location.href);
    url.searchParams.delete("playlist");
    window.history.replaceState({}, "", url.toString());
  };

  // ── Route-based code splitting (each page loads its own chunk) ──

  // Simple routing for legal pages
  if (currentPath === "/terms") return <Suspense fallback={<PageFallback />}><TermsPage /></Suspense>;
  if (currentPath === "/privacy") return <Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense>;
  if (currentPath === "/affiliate-disclosure") return <Suspense fallback={<PageFallback />}><AffiliatePage /></Suspense>;
  
  // Blog routes
  if (currentPath === "/blog") return <Suspense fallback={<PageFallback />}><BlogIndex /></Suspense>;
  if (currentPath === "/blog/convert-spotify-playlist-to-cd-vinyl") return <Suspense fallback={<PageFallback />}><ConvertSpotifyPlaylistToCd /></Suspense>;
  if (currentPath === "/blog/is-vinyl-worth-it-2025") return <Suspense fallback={<PageFallback />}><IsVinylWorthIt /></Suspense>;
  if (currentPath === "/blog/vinyl-cd-vs-streaming-true-cost") return <Suspense fallback={<PageFallback />}><VinylCdVsStreaming /></Suspense>;
  if (currentPath === "/blog/where-to-buy-vinyl-cds-2025") return <Suspense fallback={<PageFallback />}><WhereToBuyVinylCd /></Suspense>;
  if (currentPath === "/blog/essential-classic-rock-albums-vinyl") return <Suspense fallback={<PageFallback />}><EssentialClassicRockAlbums /></Suspense>;
  if (currentPath === "/blog/how-much-spotify-playlist-cost-on-vinyl") return <Suspense fallback={<PageFallback />}><SpotifyPlaylistCostOnVinyl /></Suspense>;
  if (currentPath === "/blog/spotify-playlist-to-vinyl-converter") return <Suspense fallback={<PageFallback />}><SpotifyPlaylistToVinylConverter /></Suspense>;
  if (currentPath === "/blog/how-to-buy-spotify-songs-on-cd") return <Suspense fallback={<PageFallback />}><HowToBuySpotifySongsOnCd /></Suspense>;
  if (currentPath === "/blog/cost-of-owning-music-vs-streaming") return <Suspense fallback={<PageFallback />}><CostOfOwningMusicVsStreaming /></Suspense>;
  if (currentPath === "/blog/do-you-own-your-spotify-music") return <Suspense fallback={<PageFallback />}><DoYouOwnSpotifyMusic /></Suspense>;
  if (currentPath === "/blog/spotify-songs-disappearing-from-playlist") return <Suspense fallback={<PageFallback />}><SpotifySongsDisappearing /></Suspense>;

  // Glossary routes (programmatic SEO)
  if (currentPath === "/glossary") return <Suspense fallback={<PageFallback />}><GlossaryIndex /></Suspense>;
  if (currentPath.startsWith("/glossary/")) {
    const slug = currentPath.replace("/glossary/", "");
    if (glossarySlugs.has(slug)) return <Suspense fallback={<PageFallback />}><GlossaryTermPage slug={slug} /></Suspense>;
  }

  // Compare routes (programmatic SEO)
  if (currentPath === "/compare") return <Suspense fallback={<PageFallback />}><CompareIndex /></Suspense>;
  if (currentPath.startsWith("/compare/")) {
    const slug = currentPath.replace("/compare/", "");
    if (comparisonSlugs.has(slug)) return <Suspense fallback={<PageFallback />}><ComparePage slug={slug} /></Suspense>;
  }

  // Persona routes (programmatic SEO)
  if (currentPath.startsWith("/for/")) {
    const slug = currentPath.replace("/for/", "");
    if (personaSlugs.has(slug)) return <Suspense fallback={<PageFallback />}><PersonaPage slug={slug} /></Suspense>;
  }

  // Best-of curation routes (programmatic SEO)
  if (currentPath === "/best") return <Suspense fallback={<PageFallback />}><BestIndex /></Suspense>;
  if (currentPath.startsWith("/best/")) {
    const slug = currentPath.replace("/best/", "");
    if (curationSlugs.has(slug)) return <Suspense fallback={<PageFallback />}><BestPage slug={slug} /></Suspense>;
  }

  // Guide routes (programmatic SEO)
  if (currentPath === "/guides") return <Suspense fallback={<PageFallback />}><GuideIndex /></Suspense>;
  if (currentPath.startsWith("/guides/")) {
    const slug = currentPath.replace("/guides/", "");
    if (guideSlugs.has(slug)) return <Suspense fallback={<PageFallback />}><GuidePage slug={slug} /></Suspense>;
  }

  return (
    <div className="shell">
      <HomeHead />
      <LanguageSwitcher />
      <div className="layout-bento">
        {/* Hero — uses <div> instead of <section> to avoid the deprecated
            H1UserAgentFontSizeInSection browser behavior (Chrome adjusts h1
            font-size inside sectioning elements). */}
        <div className="box hero-box" role="banner">
          <h1 className="display-text">
            {t.hero.title1} <br /> {t.hero.title2}<span className="site-brand">{SITE_CONFIG.name}</span>
          </h1>
          <p className="display-sub">
            <span className="brand-spotify">{t.hero.streamingRenting}</span> IS RENTING.
            <span className="brand-cdify">{t.hero.vinylCdForever}</span> IS FOREVER.
            <br />
            <span className="sub-description">
              {t.hero.description} <strong className="brand-cta">{t.hero.ctaName}!</strong>
            </span>
          </p>

          <PlaylistForm
            isSubmitting={isPending}
            onSubmit={handleSubmit}
            onReset={handleReset}
            initialValue={lastPlaylistRef}
          />
        </div>

        <Suspense fallback={null}>
          <RecommendationsSection
            ref={resultsRef}
            data={data}
            isLoading={isPending}
            isError={isError}
            errorMessage={error?.message}
          />
        </Suspense>

        {/* Value Props */}
        <div className="box perk-box">
          <span className="perk-icon">🔒</span>
          <div className="perk-content">
            <span className="perk-title">{t.perks.ownIt.title}</span>
            <span className="perk-desc">{t.perks.ownIt.desc}</span>
          </div>
        </div>
        <div className="box perk-box purple">
          <span className="perk-icon spin-on-hover">🎵</span>
          <div className="perk-content">
            <span className="perk-title">{t.perks.quality.title}</span>
            <span className="perk-desc">{t.perks.quality.desc}</span>
          </div>
        </div>
        <div className="box perk-box coral">
          <span className="perk-icon">📀</span>
          <div className="perk-content">
            <span className="perk-title">{t.perks.collection.title}</span>
            <span className="perk-desc">{t.perks.collection.desc}</span>
          </div>
        </div>
      </div>

      {/* Google AdSense display ad */}
      <div className="ad-container">
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2031491346868899"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>

      <FAQSection />

      <footer className="footer">
        <div className="footer-affiliate-disclosure">
          <strong>{t.footer.affiliateDisclosure}</strong> {t.affiliate.disclosureText}
        </div>
        <p>
          {t.footer.copyright} · <a href={SITE_CONFIG.companyUrl} target="_blank" rel="noopener noreferrer">{SITE_CONFIG.companyName}</a>
        </p>
        <div className="footer-links">
          <a href="/blog">Blog</a>
          <span className="footer-divider">·</span>
          <a href="/glossary">Glossary</a>
          <span className="footer-divider">·</span>
          <a href="/compare">Compare</a>
          <span className="footer-divider">·</span>
          <a href="/best">Best Of</a>
          <span className="footer-divider">·</span>
          <a href="/guides">Guides</a>
          <span className="footer-divider">·</span>
          <a href="/terms">{t.footer.terms}</a>
          <span className="footer-divider">·</span>
          <a href="/privacy">{t.footer.privacy}</a>
          <span className="footer-divider">·</span>
          <a href="/affiliate-disclosure">{t.footer.affiliateLink}</a>
          <span className="footer-divider">·</span>
          <span className="footer-note">{t.footer.notAffiliated}</span>
        </div>
      </footer>

      <SocialProof />
    </div>
  );
}

export default App;

